import {
  ComponentPropsWithoutRef,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useCompanySummaryState } from '../../hooks';
import { useTracking } from 'react-tracking';
import {
  COMPANY_VALUE_ANCHOR,
  COMPANY_FUTURE_ANCHOR,
  COMPANY_PAST_ANCHOR,
  COMPANY_HEALTH_ANCHOR,
  COMPANY_DIVIDEND_ANCHOR,
  COMPANY_ABOUT_ANCHOR,
} from '@/constants/anchors';
import { COMPANY_SNOWFLAKE } from '@/constants/feedbackSections';
import { SnowflakeType } from '@/components/DeprecatedSnowflake/SnowflakeType';
import {
  SnowflakeChart,
  RiskRewardWrapper,
  AboutIcon,
  SummarySection,
  OverviewSectionHeader,
  Description,
  SnowflakeTitle,
  SnowflakeCaption,
  SnowflakeDescription,
} from './styled';
import RiskRewardLoadingSkeleton from '../RiskRewardLoadingSkeleton';
import { Button, Grid, Text, Box, useTheme } from '@simplywallst/ui-core';
import loadable from '@loadable/component';
import SnowflakeChartWrapper from '../SnowflakeChartWrapper';
import SnowflakeTable from '../SnowflakeTable';
import RiskReward from '../RiskReward';
import RiskSummaryList from '../RiskSummaryList';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import RiskRewardHeading from '../RiskRewardHeading';
import { useVisibleSectionsContext } from '../../../VisibleSectionsContextProvider';
import { isValidSection } from '@/pages/CompanyReport/useSection';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted/useCompanyStatementsAPIFormatted';
import {
  getPassRewardList,
  getRiskList,
  getRiskRewardFragments,
  getSectionChecks,
} from '../../utils';
import { useScrollIntoView } from '@/hooks/useScrollIntoView';
import { useTranslation } from '@/hooks/useTranslation';

const RiskCheckModal = loadable(() => import('../RiskCheckModal'));

const Snowflake = loadable(() => import('@/components/DeprecatedSnowflake'));

const routes: string[] = [
  COMPANY_VALUE_ANCHOR,
  COMPANY_FUTURE_ANCHOR,
  COMPANY_PAST_ANCHOR,
  COMPANY_HEALTH_ANCHOR,
  COMPANY_DIVIDEND_ANCHOR,
];

type Props = {
  handleShowAboutCompany: (value: boolean) => void;
};

export function Summary({ handleShowAboutCompany }: Props) {
  const {
    isFund,
    shortDescription,
    snowflakePoints,
    uniqueSymbol,
    tickerSymbol,
    companyName,
  } = useCompanySummaryState();
  const canonicalURL = useCompanyCanonicalUrl();
  const { trackEvent } = useTracking({ type: 'track', subject: 'summary' });
  const [openRisksModal, setOpenRiskModal] = useState(false);
  const { loadSection } = useVisibleSectionsContext();
  const { data: statements, status: statementApiStatus } =
    useCompanyStatementsAPIFormatted({
      canonicalURL,
    });

  const {
    IsProfitable: isProfitable,
    IsProfitableLongTerm: isProfitableLongTerm,
    IsBank: isBank,
  } = statements || {};

  const isLossMaking =
    isProfitable?.value === false && isProfitableLongTerm?.value === false;

  const isFinancialCompany = isBank?.value === true;

  const sectionChecks = getSectionChecks(
    isFinancialCompany,
    isLossMaking,
    statements
  );

  const sentence = statements?.sentence;
  const riskList = getRiskList(statements);
  const passRewardsList = getPassRewardList(statements);
  const scroller = useScrollIntoView();
  const theme = useTheme();

  const t = useTranslation();

  useEffect(() => {
    trackEvent({
      action: 'landed',
    });
  }, [trackEvent]);

  const handleSnowflakeClick = useCallback<
    NonNullable<ComponentPropsWithoutRef<typeof Snowflake>['handleClick']>
  >(
    (route) => {
      if (isValidSection(route)) {
        loadSection(route);
      } else {
        console.warn('attempted to redirect to an invalid section:', route);
      }
    },
    [loadSection]
  );

  return (
    <SummarySection data-cy-id="company-summary">
      <Grid
        columnGap="8px"
        templateRows="auto 1fr"
        templateColumns="1fr 1fr"
        templateColumnsSm="1fr 200px"
        templateColumnsMd="1fr 280px"
      >
        <div>
          <OverviewSectionHeader>
            {t('page.companyReport.overview.header', { tickerSymbol })}
          </OverviewSectionHeader>
          <Description
            isEmptyState={!shortDescription}
            data-cy-id="company-summary-desc"
          >
            {shortDescription || t('page.companyReport.overview.noDescription')}
          </Description>
          <Text typography="xSmall">
            <Button
              as="a"
              styleType="strongLink"
              data-cy-id="company-about-button"
              onClick={() => {
                scroller(`#${COMPANY_ABOUT_ANCHOR}`);
                handleShowAboutCompany(true);
                trackEvent({
                  modifier: 'about-company-read-more',
                  action: 'click',
                });
              }}
            >
              <Box cursor="pointer">
                <Grid
                  templateColumns={`${theme.x3}px auto`}
                  alignItems="center"
                  gap={`${theme.x1}px`}
                >
                  <AboutIcon />
                  {t('page.companyReport.overview.about')}
                </Grid>
              </Box>
            </Button>
          </Text>
        </div>
        <Grid row="span 1" rowSm="span 2">
          <div>
            <SnowflakeChartWrapper
              id="overview-summary-snowflake"
              points={snowflakePoints || [0, 0, 0, 0, 0]}
              companyForFeedback={uniqueSymbol}
              feedbackLabel={COMPANY_SNOWFLAKE}
              chart={
                <SnowflakeChart data-cy-id="overview-summary-snowflake">
                  <Snowflake
                    points={snowflakePoints}
                    detailedPoints={sectionChecks}
                    tooltip={true}
                    routes={routes}
                    handleClick={handleSnowflakeClick}
                    disabled={isFund}
                    type={SnowflakeType.COMPANY}
                  />
                  <SnowflakeDescription>
                    <SnowflakeTitle>
                      {t('page.companyReport.overview.snowflake')}
                    </SnowflakeTitle>
                    {sentence && !isFund && (
                      <SnowflakeCaption data-cy-id="company-fundamentals-desc">
                        {sentence}
                      </SnowflakeCaption>
                    )}
                  </SnowflakeDescription>
                </SnowflakeChart>
              }
              table={
                <SnowflakeChart>
                  <SnowflakeTable
                    points={snowflakePoints || [0, 0, 0, 0, 0]}
                    tickerSymbol={tickerSymbol}
                  />
                </SnowflakeChart>
              }
            />
          </div>
        </Grid>
        <Grid column="span 2" columnSm="span 1">
          <RiskRewardWrapper data-cy-id="risk-reward-wrapper">
            {statementApiStatus === 'success' ? (
              <>
                {!isFund && (
                  <>
                    <RiskRewardHeading>
                      {t('page.companyReport.overview.rewards')}
                    </RiskRewardHeading>
                    {passRewardsList.map((reward, i) => {
                      const { fragment } = getRiskRewardFragments(
                        reward.name,
                        ''
                      );
                      return (
                        <RiskReward
                          key={reward.name}
                          name={reward.name}
                          index={i}
                          hash={fragment}
                          description={reward.description}
                        />
                      );
                    })}
                  </>
                )}
                {!isFund && (
                  <>
                    <RiskRewardHeading>
                      {t('page.companyReport.overview.risk')}
                    </RiskRewardHeading>
                    <RiskSummaryList
                      risks={riskList}
                      tickerSymbol={tickerSymbol}
                    />
                    <Button
                      displayXsDown="block"
                      onClick={() => {
                        setOpenRiskModal(true);
                        trackEvent({
                          modifier: 'see-all-risk-checks',
                          action: 'click',
                        });
                      }}
                      styleType="loud"
                      data-cy-id="summary-risk-modal-button"
                    >
                      {t('page.companyReport.overview.seeAllRisks')}
                    </Button>
                  </>
                )}
              </>
            ) : (
              <>
                <RiskRewardHeading>
                  {t('page.companyReport.overview.rewards')}
                </RiskRewardHeading>
                <RiskRewardLoadingSkeleton data-cy-id="reward-loading-skeleton" />
                <RiskRewardHeading>
                  {t('page.companyReport.overview.risk')}
                </RiskRewardHeading>
                <RiskRewardLoadingSkeleton data-cy-id="risk-loading-skeleton" />
              </>
            )}
            {openRisksModal && (
              <RiskCheckModal
                companyShortName={companyName}
                companyTickerSymbol={tickerSymbol}
                risks={riskList}
                onClose={() => setOpenRiskModal(false)}
              />
            )}
          </RiskRewardWrapper>
        </Grid>
      </Grid>
    </SummarySection>
  );
}
